import React, { useCallback, useState } from 'react'
import type { PlayerAPI } from 'bitmovin-player'
import { Case, Switch } from 'react-if'
import { BitAngelPlayer } from '@/organisms/BitAngelPlayer'
import { StoryVideoPlayerUI } from '@/organisms/BitAngelPlayer/StoryVideoPlayerUI'
import { StoryPlayerActions } from '@/organisms/StoryPlayer/StoryPlayerActions'
import { TextStoryPlayer } from '@/organisms/StoryPlayer/TextStoryPlayer'
import { isTextStory, isVideoStory, PlayableStory } from '@/utils/StoryUtil/StoryUtil'
import { useSafeTrack } from '@/utils/analytics'

interface StoryPlayerProps {
  story: PlayableStory
  isOverlayMode?: boolean
}

export const MomentStoryPlayer: React.FC<StoryPlayerProps> = ({ isOverlayMode = false, story }) => {
  const track = useSafeTrack()
  const [player, setPlayer] = useState<PlayerAPI>()

  const trackVideoStoryStarted = useCallback(() => {
    track('Moment Story Started', {
      type: 'video',
      videoUri: story.videoUri,
      storyUserId: story.userId,
    })
  }, [track, story])

  const trackVideoStoryFinished = useCallback(() => {
    track('Moment Story Finished', {
      type: 'video',
      videoUri: story.videoUri,
      storyUserId: story.userId,
    })
  }, [track, story])

  const trackTextStoryStarted = useCallback(() => {
    track('Moment Story Started', {
      type: 'text',
      text: story.text,
      storyUserId: story.userId,
    })
  }, [track, story])

  const handlePlayerInit = useCallback((player: PlayerAPI) => {
    setPlayer(player)
  }, [])

  const handleFullscreenToggle = useCallback(
    (isFullscreen: boolean) => {
      if (isFullscreen) {
        player?.pause()
      } else {
        player?.play()
      }
    },
    [player],
  )

  const handleReportUserToggled = useCallback(
    (isShowing: boolean) => {
      if (isShowing) {
        player?.pause()
      } else {
        player?.play()
      }
    },
    [player],
  )

  const videoAspect = isOverlayMode ? 'aspect-w-1 aspect-h-1' : 'aspect-w-9 aspect-h-16'

  const actionContainerClasses = isOverlayMode ? 'block' : 'absolute bottom-6 right-6 pb-6 z-20'

  const textPlayerClassName = isOverlayMode
    ? 'aspect-w-9 aspect-h-11'
    : ' aspect-w-9 aspect-h-12 sm:aspect-h-16 2xl:aspect-h-12'

  return (
    <>
      <Switch>
        <Case condition={isVideoStory(story)}>
          <BitAngelPlayer
            autoplay={false}
            onStart={trackVideoStoryStarted}
            onPlaybackFinished={trackVideoStoryFinished}
            className={videoAspect}
            customStyles="max-h-[75vh]"
            ui={StoryVideoPlayerUI}
            url={story?.videoUri as string}
            hideFullscreen
            onPlayerInit={handlePlayerInit}
          >
            {!isOverlayMode && (
              <div className={actionContainerClasses}>
                <StoryPlayerActions
                  onFullscreenToggled={handleFullscreenToggle}
                  onReportUserToggled={handleReportUserToggled}
                  story={story}
                  isOverlayMode={isOverlayMode}
                />
              </div>
            )}
          </BitAngelPlayer>
        </Case>
        <Case condition={isTextStory(story)}>
          <TextStoryPlayer className={textPlayerClassName} text={story.text as string} onStart={trackTextStoryStarted}>
            {!isOverlayMode && (
              <div className="relative mt-4">
                <div className={actionContainerClasses}>
                  <StoryPlayerActions
                    onFullscreenToggled={handleFullscreenToggle}
                    onReportUserToggled={handleReportUserToggled}
                    story={story}
                    isOverlayMode={isOverlayMode}
                  />
                </div>
              </div>
            )}
          </TextStoryPlayer>
        </Case>
      </Switch>
      {isOverlayMode && (
        <div className="relative mt-4">
          <div className={actionContainerClasses}>
            <StoryPlayerActions
              onFullscreenToggled={handleFullscreenToggle}
              onReportUserToggled={handleReportUserToggled}
              story={story}
              isOverlayMode={isOverlayMode}
            />
          </div>
        </div>
      )}
    </>
  )
}
